import { useState, useEffect } from 'react';
import apiClient from '@/utils/apiClient';
import downloadFile from '@/utils/fileDownloader';
import { toast } from 'sonner';
import {
  Eye,
  Filter,
  X,
  Clock,
  CheckCircle,
  Calendar,
  AlertCircle,
  Edit2,
  Trash2,
  Paperclip,
  Download,
  ExternalLink,
  Search,
  RefreshCw,
  Plus,
  Users,
  CheckCircle2,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import TinyRichTextEditor from '@/components/shared/TinyRichTextEditor';
import RichTextViewer, { stripHtml } from '@/components/shared/RichTextViewer';

const getAttachmentUrl = (rawUrl) => {
  if (!rawUrl) return '';
  if (typeof rawUrl === 'object' && rawUrl.url) return getAttachmentUrl(rawUrl.url);
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) return rawUrl;
  const backendBase = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  return `${backendBase}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
};

const getDownloadUrl = (rawUrl) => {
  const url = getAttachmentUrl(rawUrl);
  if (url.includes('/api/v1/common/attachments/')) {
    return url.includes('?') ? `${url}&download=true` : `${url}?download=true`;
  }
  return url;
};

const getCleanFilename = (rawUrl, fallbackIndex = 0) => {
  if (!rawUrl) return `Attachment ${fallbackIndex + 1}`;
  if (typeof rawUrl === 'object' && rawUrl.name) return rawUrl.name;
  try {
    const rawName = decodeURIComponent(String(rawUrl).split('?')[0].split('/').pop() || '');
    const parts = rawName.split('_');
    if (parts.length > 1 && parts[0].length === 12) {
      return parts.slice(1).join('_');
    }
    return rawName || `Attachment ${fallbackIndex + 1}`;
  } catch (e) {
    return `Attachment ${fallbackIndex + 1}`;
  }
};

const AnnouncementHistory = ({ hostelId, refreshTrigger }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');

  // Modals state
  const [viewModal, setViewModal] = useState(null);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Edit Modal State
  const [editModal, setEditModal] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    message: '',
    category: 'general',
    priority: 'medium',
    attachments: []
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // Delete Modal State
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (hostelId) {
      fetchAnnouncements();
    }
  }, [hostelId, refreshTrigger]);

  useEffect(() => {
    applyFilters();
  }, [announcements, searchQuery, selectedCategory, selectedPriority]);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/v1/admin/announcements/', {
        params: { hostel_id: hostelId }
      });
      const data = Array.isArray(response.data) ? response.data : response.data.announcements || [];
      setAnnouncements(data);
    } catch (error) {
      toast.error('Failed to load announcement history');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (announcementId) => {
    setLoadingStats(true);
    setStats(null);
    try {
      const response = await apiClient.get(`/v1/admin/announcements/${announcementId}/stats`, {
        params: { hostel_id: hostelId }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleViewDetails = (announcement) => {
    setViewModal(announcement);
    fetchStats(announcement.id);
  };

  // Open Edit Dialog
  const handleOpenEdit = (announcement, e) => {
    if (e) e.stopPropagation();
    setEditModal(announcement);
    setEditFormData({
      title: announcement.title || '',
      message: announcement.message || '',
      category: announcement.category || 'general',
      priority: announcement.priority || 'medium',
      attachments: Array.isArray(announcement.attachments) ? [...announcement.attachments] : []
    });
  };

  // Upload new attachment during edit
  const handleEditFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingAttachment(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await apiClient.post('/v1/admin/uploads/attachment', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
      });

      const results = await Promise.all(uploadPromises);
      const newUrls = results.map(r => r.url || r.view_url);
      setEditFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newUrls]
      }));
      toast.success(`${results.length} attachment(s) uploaded to Cloudflare R2`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      e.target.value = '';
    }
  };

  // Remove attachment during edit
  const handleRemoveEditAttachment = (index) => {
    setEditFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  // Submit Edit
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editFormData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!editFormData.message.trim()) {
      toast.error('Message is required');
      return;
    }

    setSavingEdit(true);
    try {
      const payload = {
        title: editFormData.title,
        message: editFormData.message,
        category: editFormData.category,
        priority: editFormData.priority,
        attachments: editFormData.attachments
      };

      const res = await apiClient.put(`/v1/admin/announcements/${editModal.id}`, payload);
      const updatedNotice = res.data;

      // Update state locally
      setAnnouncements(prev => prev.map(a =>
        a.id === editModal.id ? { ...a, ...payload, updated_at: updatedNotice.updated_at } : a
      ));

      if (viewModal && viewModal.id === editModal.id) {
        setViewModal(prev => ({ ...prev, ...payload }));
      }

      toast.success('Notice updated successfully!');
      setEditModal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update notice');
    } finally {
      setSavingEdit(false);
    }
  };

  // Open Delete Confirmation Dialog
  const handleOpenDelete = (announcement, e) => {
    if (e) e.stopPropagation();
    setDeleteModal(announcement);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/v1/admin/announcements/${deleteModal.id}`);
      setAnnouncements(prev => prev.filter(a => a.id !== deleteModal.id));
      if (viewModal && viewModal.id === deleteModal.id) {
        setViewModal(null);
      }
      toast.success('Notice deleted successfully');
      setDeleteModal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete notice');
    } finally {
      setDeleting(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...announcements];

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        (a.title && a.title.toLowerCase().includes(q)) ||
        (a.message && a.message.toLowerCase().includes(q))
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(a => (a.category || 'general').toLowerCase() === selectedCategory.toLowerCase());
    }

    // Priority filter
    if (selectedPriority !== 'all') {
      filtered = filtered.filter(a => (a.priority || 'medium').toLowerCase() === selectedPriority.toLowerCase());
    }

    setFilteredAnnouncements(filtered);
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return (
      <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${colors[priority] || colors.medium}`}>
        {priority?.toUpperCase() || 'MEDIUM'}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const colors = {
      general: 'bg-purple-100 text-purple-700 border-purple-200',
      financial: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      holidays: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return (
      <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border capitalize ${colors[category] || colors.general}`}>
        {category || 'general'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const configs = {
      sent: { color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle, label: 'Sent' },
      scheduled: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock, label: 'Scheduled' },
      draft: { color: 'bg-gray-100 text-gray-700 border-gray-200', icon: AlertCircle, label: 'Draft' }
    };
    const config = configs[status] || configs.sent;
    const Icon = config.icon;
    return (
      <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border flex items-center gap-1 ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getPriorityAccent = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-red-500';
      case 'medium': return 'border-l-amber-500';
      case 'low': return 'border-l-blue-500';
      default: return 'border-l-gray-300';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getAudienceLabel = (targeting) => {
    if (!targeting) return 'All Parents';
    if (targeting.target_all) return 'All Parents';
    if (targeting.target_classes?.length > 0) return `${targeting.target_classes.length} Class(es)`;
    if (targeting.target_rooms?.length > 0) return `${targeting.target_rooms.length} Room(s)`;
    if (targeting.target_students?.length > 0) return `${targeting.target_students.length} Student(s)`;
    if (targeting.target_warden_id) return 'Warden Group';
    return 'Targeted Parents';
  };

  const categoryCounts = {
    all: announcements.length,
    general: announcements.filter(a => (a.category || 'general') === 'general').length,
    financial: announcements.filter(a => a.category === 'financial').length,
    holidays: announcements.filter(a => a.category === 'holidays').length,
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b">
        <div>
          <h2 className="text-xl font-bold text-[#2C5F7F] flex items-center gap-2">
            Notice History & Management
            <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-semibold border border-blue-200">
              {filteredAnnouncements.length} {filteredAnnouncements.length === 1 ? 'Notice' : 'Notices'}
            </span>
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Manage, edit, or delete existing notices sent to students and parents
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAnnouncements}
            disabled={loading}
            className="text-gray-600 hover:text-gray-900"
            title="Refresh Notice List"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center justify-between">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by title or message..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7F] bg-gray-50/50"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Category Pills & Priority Filter */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Category Pills */}
          <div className="flex bg-gray-100 p-1 rounded-lg text-xs">
            {['all', 'general', 'financial', 'holidays'].map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-md font-medium capitalize transition-all ${selectedCategory === cat
                    ? 'bg-white text-[#2C5F7F] shadow-xs'
                    : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                {cat} ({categoryCounts[cat] || 0})
              </button>
            ))}
          </div>

          {/* Priority Select */}
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="px-3 py-1.5 text-xs border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#2C5F7F]"
          >
            <option value="all">All Priorities</option>
            <option value="high">High Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="low">Low Priority</option>
          </select>
        </div>
      </div>

      {/* Notices List */}
      {loading ? (
        <div className="py-16 text-center space-y-3">
          <div className="w-9 h-9 border-3 border-[#2C5F7F] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-sm text-gray-500 font-medium">Loading notices...</p>
        </div>
      ) : filteredAnnouncements.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-xl bg-gray-50/50">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-semibold">No notices found</p>
          <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
            {searchQuery || selectedCategory !== 'all' || selectedPriority !== 'all'
              ? 'No notices match the active search or filters. Try adjusting your filters.'
              : 'No notices have been published yet. Use the Create Announcement button above to publish your first notice.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAnnouncements.map((announcement) => {
            const category = announcement.category || 'general';
            const priority = announcement.priority || 'medium';
            const attachments = announcement.attachments || [];
            const readCount = Math.min(
              announcement.recipient_count || announcement.recipients?.length || 0,
              announcement.read_count ?? (announcement.read_by?.length || 0)
            );
            const totalRecipients = announcement.recipient_count || announcement.recipients?.length || 0;

            return (
              <div
                key={announcement.id}
                className={`group border rounded-xl p-2 bg-white transition-all hover:shadow-md border-l-4 ${getPriorityAccent(priority)}`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                  <div className="flex-1 space-y-2">
                    {/* Header Badges */}
                    <div className="flex flex-wrap items-center gap-2">
                      {getCategoryBadge(category)}
                      {getPriorityBadge(priority)}
                      {getStatusBadge(announcement.status)}
                      <span className="text-xs text-gray-500 flex items-center gap-1 ml-auto md:ml-0">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDate(announcement.created_at)}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-bold text-base text-gray-900 group-hover:text-[#2C5F7F] transition-colors">
                      {announcement.title}
                    </h3>

                    {/* Message Preview */}
                    <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                      {stripHtml(announcement.message)}
                    </p>

                    {/* Metadata & Attachments row */}
                    <div className="flex flex-wrap items-center gap-3 pt-1 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-2.5 py-1 rounded-md font-medium">
                        <Users className="w-3.5 h-3.5 text-gray-500" />
                        Audience: {getAudienceLabel(announcement.targeting)}
                      </span>

                      <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md font-medium border border-blue-100">
                        <CheckCircle2 className="w-3.5 h-3.5 text-blue-500" />
                        {readCount} of {totalRecipients} Read
                      </span>

                      {/* Attachment Chips */}
                      {attachments.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {attachments.map((att, i) => {
                            const name = getCleanFilename(att, i);
                            const url = getAttachmentUrl(att);
                            return (
                              <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 font-medium transition-colors"
                                title="Click to view attachment"
                              >
                                <Paperclip className="w-3 h-3 text-purple-600" />
                                <span className="max-w-[140px] truncate">{name}</span>
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions Buttons */}
                  <div className="flex items-center gap-1.5 self-end md:self-start pt-2 md:pt-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(announcement)}
                      className="text-[#2C5F7F] border-[#2C5F7F]/30 hover:bg-[#2C5F7F]/10 hover:text-[#2C5F7F] h-8 px-2.5 text-xs"
                      title="View notice details and read receipts"
                    >
                      <Eye className="w-3.5 h-3.5 mr-1" />
                      View
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => handleOpenEdit(announcement, e)}
                      className="text-amber-700 border-amber-300 hover:bg-amber-50 h-8 px-2.5 text-xs"
                      title="Edit this notice"
                    >
                      <Edit2 className="w-3.5 h-3.5 mr-1" />
                      Edit
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => handleOpenDelete(announcement, e)}
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 h-8 px-2.5 text-xs"
                      title="Delete this notice"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ==================== VIEW DETAILS MODAL ==================== */}
      {viewModal && (
        <Dialog open={Boolean(viewModal)} onOpenChange={(open) => !open && setViewModal(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {getCategoryBadge(viewModal.category)}
                {getPriorityBadge(viewModal.priority)}
                {getStatusBadge(viewModal.status)}
              </div>
              <DialogTitle className="text-xl font-bold text-[#2C5F7F]">
                {viewModal.title}
              </DialogTitle>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                <Clock className="w-3.5 h-3.5" />
                Created on {formatDate(viewModal.created_at)}
                {viewModal.updated_at && (
                  <span className="italic">
                    • Edited {formatDate(viewModal.updated_at)}
                  </span>
                )}
              </div>
            </DialogHeader>

            <div className="space-y-5 py-2">
              {/* Notice Message */}
              <div className="bg-gray-50 p-4.5 rounded-xl border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notice Content</p>
                <RichTextViewer content={viewModal.message} />
              </div>

              {/* Attachments Section */}
              {viewModal.attachments && viewModal.attachments.length > 0 && (
                <div className="space-y-2 border-t pt-4">
                  <h4 className="font-bold text-sm text-gray-800 flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-[#2C5F7F]" />
                    Attachments ({viewModal.attachments.length})
                  </h4>
                  <div className="grid gap-2">
                    {viewModal.attachments.map((att, index) => {
                      const name = getCleanFilename(att, index);
                      const viewUrl = getAttachmentUrl(att);
                      const downloadUrl = getDownloadUrl(att);
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-blue-200 transition-colors"
                        >
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="p-2 bg-blue-50 text-[#2C5F7F] rounded-md">
                              <Paperclip className="w-4 h-4" />
                            </div>
                            <span className="text-sm font-medium text-gray-800 truncate max-w-sm">
                              {name}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 ml-4">
                            <a
                              href={viewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              View
                            </a>
                            <button
                              type="button"
                              onClick={() => downloadFile(downloadUrl, name)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold bg-[#2C5F7F] text-white hover:bg-[#234d67] transition-colors"
                            >
                              <Download className="w-3.5 h-3.5" />
                              Download
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Targeting Scope */}
              {viewModal.targeting && (
                <div className="border-t pt-4">
                  <h4 className="font-bold text-sm text-gray-800 mb-2">Target Audience</h4>
                  <div className="text-xs space-y-1 bg-gray-50 p-3 rounded-lg border text-gray-600">
                    {viewModal.targeting.target_all && <p>• All parents of this hostel</p>}
                    {viewModal.targeting.target_classes?.length > 0 && (
                      <p>• Classes: {viewModal.targeting.target_classes.join(', ')}</p>
                    )}
                    {viewModal.targeting.target_rooms?.length > 0 && (
                      <p>• Rooms: {viewModal.targeting.target_rooms.join(', ')}</p>
                    )}
                    {viewModal.targeting.target_students?.length > 0 && (
                      <p>• Selected Students: {viewModal.targeting.target_students.length}</p>
                    )}
                    {viewModal.targeting.target_warden_id && (
                      <p>• Students assigned to Warden</p>
                    )}
                  </div>
                </div>
              )}

              {/* Read Receipts */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-sm text-gray-800 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Read Receipts
                  </h4>
                  {stats && (
                    <span className="text-xs text-gray-500 font-medium">
                      {stats.read_count} of {stats.total_count} read
                    </span>
                  )}
                </div>

                {loadingStats ? (
                  <div className="text-center py-6 text-xs text-gray-500 animate-pulse">
                    Loading recipient details...
                  </div>
                ) : stats ? (
                  <div className="space-y-4">
                    {/* Read & Unread metrics */}
                    <div className="grid grid-cols-2 gap-3 text-center">
                      <div className="bg-emerald-50 border border-emerald-100 p-2.5 rounded-lg">
                        <p className="text-lg font-bold text-emerald-700">{stats.read_count}</p>
                        <p className="text-[10px] uppercase font-bold text-emerald-600 tracking-wider">Already Read</p>
                      </div>
                      <div className="bg-red-50 border border-red-100 p-2.5 rounded-lg">
                        <p className="text-lg font-bold text-red-700">{stats.unread?.length || 0}</p>
                        <p className="text-[10px] uppercase font-bold text-red-600 tracking-wider">Pending</p>
                      </div>
                    </div>

                    {/* Unread Section */}
                    {stats.unread?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-2">
                          Not Read Yet ({stats.unread.length})
                        </p>
                        <div className="grid gap-2 max-h-[180px] overflow-y-auto pr-1">
                          {stats.unread.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-red-50/60 rounded text-xs border border-red-100">
                              <div>
                                <p className="font-semibold text-gray-800">{item.student_name}</p>
                                <p className="text-gray-500 text-[10px]">Class: {item.class}</p>
                              </div>
                              <p className="text-red-700 font-medium italic text-[11px]">Parent: {item.parent_name}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Read Section */}
                    {stats.read?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-2">
                          Already Read ({stats.read.length})
                        </p>
                        <div className="grid gap-2 max-h-[180px] overflow-y-auto pr-1">
                          {stats.read.map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-emerald-50/60 rounded text-xs border border-emerald-100">
                              <div>
                                <p className="font-semibold text-gray-800">{item.student_name}</p>
                                <p className="text-gray-500 text-[10px]">Class: {item.class}</p>
                              </div>
                              <p className="text-emerald-700 font-medium text-[11px]">Parent: {item.parent_name}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 text-center py-4">No recipient breakdown available.</p>
                )}
              </div>
            </div>

            <DialogFooter className="flex items-center justify-between border-t pt-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const noticeToEdit = viewModal;
                    setViewModal(null);
                    handleOpenEdit(noticeToEdit);
                  }}
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                >
                  <Edit2 className="w-3.5 h-3.5 mr-1" />
                  Edit Notice
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const noticeToDelete = viewModal;
                    setViewModal(null);
                    handleOpenDelete(noticeToDelete);
                  }}
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Delete
                </Button>
              </div>

              <Button onClick={() => setViewModal(null)} className="bg-gray-100 text-gray-700 hover:bg-gray-200 border-none">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* ==================== EDIT NOTICE MODAL ==================== */}
      {editModal && (
        <Dialog open={Boolean(editModal)} onOpenChange={(open) => !open && setEditModal(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#2C5F7F]">
                Edit Notice
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSaveEdit} className="space-y-4 py-2">
              {/* Category & Priority */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Category *
                  </label>
                  <select
                    value={editFormData.category}
                    onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7F]"
                    required
                  >
                    <option value="general">General</option>
                    <option value="financial">Financial</option>
                    <option value="holidays">Holidays</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Priority *
                  </label>
                  <select
                    value={editFormData.priority}
                    onChange={(e) => setEditFormData({ ...editFormData, priority: e.target.value })}
                    className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7F]"
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Notice Title *
                </label>
                <input
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2C5F7F]"
                  placeholder="Enter notice title"
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                  Notice Message *
                </label>
                <TinyRichTextEditor
                  value={editFormData.message}
                  onChange={(value) => setEditFormData((prev) => ({ ...prev, message: value }))}
                  placeholder="Enter notice message..."
                  minHeight="140px"
                  required
                />
              </div>

              {/* Manage Attachments */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-semibold text-gray-700">
                    Attachments (Stored in Cloudflare R2)
                  </label>
                  <label className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-[#2C5F7F] hover:bg-blue-100 rounded-md text-xs font-semibold cursor-pointer border border-blue-200">
                    <Plus className="w-3.5 h-3.5" />
                    {uploadingAttachment ? 'Uploading...' : 'Add Attachment'}
                    <input
                      type="file"
                      multiple
                      onChange={handleEditFileUpload}
                      disabled={uploadingAttachment}
                      className="hidden"
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt"
                    />
                  </label>
                </div>

                {editFormData.attachments.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No attachments attached to this notice.</p>
                ) : (
                  <div className="space-y-2">
                    {editFormData.attachments.map((att, idx) => {
                      const name = getCleanFilename(att, idx);
                      const viewUrl = getAttachmentUrl(att);
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2.5 bg-gray-50 border rounded-lg text-xs"
                        >
                          <div className="flex items-center gap-2 truncate">
                            <Paperclip className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <a
                              href={viewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline truncate"
                            >
                              {name}
                            </a>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveEditAttachment(idx)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title="Remove attachment"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <DialogFooter className="border-t pt-4 flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditModal(null)}
                  disabled={savingEdit}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={savingEdit || uploadingAttachment}
                  className="bg-[#2C5F7F] text-white hover:bg-[#234d67]"
                >
                  {savingEdit ? 'Saving Changes...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* ==================== DELETE CONFIRMATION MODAL ==================== */}
      {deleteModal && (
        <Dialog open={Boolean(deleteModal)} onOpenChange={(open) => !open && setDeleteModal(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 mb-2">
                <Trash2 className="w-6 h-6" />
              </div>
              <DialogTitle className="text-lg font-bold text-gray-900">
                Delete Notice
              </DialogTitle>
            </DialogHeader>

            <div className="py-2 text-sm text-gray-600 space-y-2">
              <p>
                Are you sure you want to delete <span className="font-semibold text-gray-900">"{deleteModal.title}"</span>?
              </p>
              <p className="text-xs text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-100">
                ⚠️ This notice will be permanently deleted and removed from parent and warden dashboards. Any attachments in Cloudflare R2 will also be removed.
              </p>
            </div>

            <DialogFooter className="flex gap-2 justify-end pt-3 border-t">
              <Button
                variant="outline"
                onClick={() => setDeleteModal(null)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {deleting ? 'Deleting...' : 'Yes, Delete Notice'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AnnouncementHistory;